import type { PackConfig, PackBuilderResult, BatteryChemistry } from '@brt/shared-types'

export function calculatePackTotals(pack: PackConfig): PackBuilderResult {
  if (pack.cellsInSeries <= 0) {
    throw new Error('Cells in series must be greater than 0')
  }
  if (pack.cellsInParallel <= 0) {
    throw new Error('Cells in parallel must be greater than 0')
  }

  const totalVoltage = pack.cellsInSeries * pack.singleCellVoltage
  const totalCapacityAh = pack.cellsInParallel * pack.singleCellCapacity
  const totalEnergyWh = totalVoltage * totalCapacityAh
  const totalCells = pack.cellsInSeries * pack.cellsInParallel

  const warnings: string[] = []
  if (totalVoltage > 60) {
    warnings.push(`Total voltage ${totalVoltage.toFixed(1)}V exceeds 60V safety threshold`)
  }
  if (totalCells > 500) {
    warnings.push(`Total cell count ${totalCells} exceeds 500 — consider pack management complexity`)
  }

  const configuration = `${pack.cellsInSeries}S${pack.cellsInParallel}P`

  return {
    totalVoltage: Math.round(totalVoltage * 10000) / 10000,
    totalCapacityAh: Math.round(totalCapacityAh * 10000) / 10000,
    totalEnergyWh: Math.round(totalEnergyWh * 100) / 100,
    totalCells,
    configuration,
    warnings,
    packConfig: pack,
  }
}

export interface SuggestedPackCell {
  singleCellVoltage: number
  singleCellCapacity: number
  chemistry: BatteryChemistry
}

export interface SuggestedPack {
  cellsInSeries: number
  cellsInParallel: number
  totals: PackBuilderResult
}

export function suggestPackForCapacity(
  targetAh: number,
  targetVoltage: number,
  cell: SuggestedPackCell,
): SuggestedPack | null {
  if (targetAh <= 0 || targetVoltage <= 0 || cell.singleCellVoltage <= 0 || cell.singleCellCapacity <= 0) {
    return null
  }

  const cellsInSeries = Math.ceil(targetVoltage / cell.singleCellVoltage)

  for (let cellsInParallel = 1; cellsInParallel <= 100; cellsInParallel++) {
    const totals = calculatePackTotals({
      cellsInSeries,
      cellsInParallel,
      singleCellCapacity: cell.singleCellCapacity,
      singleCellVoltage: cell.singleCellVoltage,
      chemistry: cell.chemistry,
    })
    if (totals.totalCapacityAh >= targetAh) {
      return { cellsInSeries, cellsInParallel, totals }
    }
  }

  return null
}
